import { fromBinary, toBinary } from '@bufbuild/protobuf';

import {
  PairingApprovedV1Schema,
  PairingOfferV1Schema,
  PhoneKeyResponseV1Schema,
} from '@mlo-local/protocol/generated/mlo/foundation/v1/foundation_pb.js';
import type {
  PairingApprovedV1,
  PhoneKeyResponseV1,
} from '@mlo-local/protocol/generated/mlo/foundation/v1/foundation_pb.js';
import {
  deriveBrowserSharedSecret,
  deriveSessionChannelMaterial,
  sha256,
  type SessionChannel,
  type SessionChannelMaterial,
} from '@mlo-local/protocol/crypto.js';
import { BrowserSignalingClient, type RelaySignalPacket } from './signaling.js';
import type { BrowserPairingOffer } from './pairing.js';

export type BrowserIceCandidate = {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
};

export type BrowserSessionDescription = {
  type: 'offer' | 'answer' | 'pranswer' | 'rollback';
  sdp?: string;
};

export interface BrowserDataChannel {
  /** WebRTC label negotiated by the peer; v1 uses the physical protocol lane name. */
  readonly label?: string;
  readyState: 'connecting' | 'open' | 'closing' | 'closed';
  binaryType?: 'blob' | 'arraybuffer';
  send(data: ArrayBuffer | Uint8Array): void;
  addEventListener(
    type: 'open' | 'message' | 'close' | 'error',
    listener: (event: unknown) => void,
  ): void;
  removeEventListener(
    type: 'open' | 'message' | 'close' | 'error',
    listener: (event: unknown) => void,
  ): void;
  close(): void;
}

export interface BrowserPeerConnection {
  connectionState?: string;
  localDescription?: BrowserSessionDescription | null;
  onicecandidate: ((event: { candidate: BrowserIceCandidate | null }) => void) | null;
  ondatachannel: ((event: { channel: BrowserDataChannel }) => void) | null;
  onconnectionstatechange: (() => void) | null;
  createDataChannel(
    label: string,
    options?: {
      ordered?: boolean;
      maxPacketLifeTime?: number;
      negotiated?: boolean;
      id?: number;
    },
  ): BrowserDataChannel;
  createOffer(): Promise<BrowserSessionDescription>;
  setLocalDescription(description: BrowserSessionDescription): Promise<void>;
  setRemoteDescription(description: BrowserSessionDescription): Promise<void>;
  addIceCandidate(candidate: BrowserIceCandidate): Promise<void>;
  close(): void;
}

export type BrowserIceTransportPolicy = 'all' | 'relay';

export type BrowserPeerFactory = (
  iceServers: readonly RTCIceServer[],
  iceTransportPolicy: BrowserIceTransportPolicy,
) => BrowserPeerConnection;

export type BrowserSessionChannelMaterials = {
  browserToPhone: Record<SessionChannel, SessionChannelMaterial>;
  phoneToBrowser: Record<SessionChannel, SessionChannelMaterial>;
};

export type BrowserSessionHandshakeMaterial = {
  browserPublicKeyFingerprint: Uint8Array;
  phonePublicKeyFingerprint: Uint8Array;
  offerHash: Uint8Array;
};

export type BrowserWebRtcSessionOptions = {
  pairing: BrowserPairingOffer;
  iceServers?: readonly RTCIceServer[];
  /** Use `relay` for a deterministic TURN-only validation run. */
  iceTransportPolicy?: BrowserIceTransportPolicy;
  socketFactory?: ConstructorParameters<typeof BrowserSignalingClient>[0]['socketFactory'];
  peerFactory?: BrowserPeerFactory;
  onDataChannel?: (channel: BrowserDataChannel, lane: SessionChannel) => void;
  onState?: (state: BrowserWebRtcSessionState) => void;
  onError?: (error: Error) => void;
};

export type BrowserWebRtcSessionState =
  | 'DISCONNECTED'
  | 'SIGNALING'
  | 'WAITING_PHONE'
  | 'APPROVAL_RECEIVED'
  | 'NEGOTIATING'
  | 'CONNECTED'
  | 'FAILED';

/** Browser-side ephemeral WebRTC owner. It retains no application state beyond this tab. */
export class BrowserWebRtcSession {
  private readonly pairing: BrowserPairingOffer;
  private readonly peerFactory: BrowserPeerFactory;
  private readonly iceServers: readonly RTCIceServer[];
  private readonly iceTransportPolicy: BrowserIceTransportPolicy;
  private readonly signaling: BrowserSignalingClient;
  private state: BrowserWebRtcSessionState = 'DISCONNECTED';
  private peer: BrowserPeerConnection | null = null;
  private remoteDescriptionSet = false;
  private pendingRemoteIceCandidates: BrowserIceCandidate[] = [];
  private readonly channels = new Map<SessionChannel, BrowserDataChannel>();
  private keyResponse: PhoneKeyResponseV1 | null = null;
  private approval: PairingApprovedV1 | null = null;
  private materials: BrowserSessionChannelMaterials | null = null;
  private handshakeMaterialValue: BrowserSessionHandshakeMaterial | null = null;
  private readonly channelWaiters = new Map<
    SessionChannel,
    Set<(channel: BrowserDataChannel) => void>
  >();
  private packetChain = Promise.resolve();

  constructor(private readonly options: BrowserWebRtcSessionOptions) {
    this.pairing = options.pairing;
    this.iceServers = validateIceServers(options.iceServers ?? []);
    this.iceTransportPolicy = options.iceTransportPolicy ?? 'all';
    if (
      this.iceTransportPolicy === 'relay' &&
      !this.iceServers.some((server) =>
        (Array.isArray(server.urls) ? server.urls : [server.urls]).some((url) =>
          /^(turn|turns):/u.test(url),
        ),
      )
    ) {
      throw new Error('TURN_SERVERS_REQUIRED');
    }
    this.peerFactory =
      options.peerFactory ??
      ((iceServers, iceTransportPolicy) =>
        new RTCPeerConnection({
          iceServers: [...iceServers],
          iceTransportPolicy,
        }) as unknown as BrowserPeerConnection);
    this.signaling = new BrowserSignalingClient({
      signalingOrigin: this.pairing.offer.signalingOrigin,
      sessionId: this.pairing.offer.sessionId,
      ...(options.socketFactory ? { socketFactory: options.socketFactory } : {}),
      onPacket: (packet) => this.enqueuePacket(packet),
      onClose: () => {
        if (this.state !== 'DISCONNECTED' && this.state !== 'FAILED') this.setState('DISCONNECTED');
      },
    });
  }

  get status(): BrowserWebRtcSessionState {
    return this.state;
  }

  get sessionId(): Uint8Array {
    return new Uint8Array(this.pairing.offer.sessionId);
  }

  get dataChannel(): BrowserDataChannel | null {
    return this.channels.get('control') ?? null;
  }

  getDataChannel(lane: SessionChannel = 'control'): BrowserDataChannel | null {
    return this.channels.get(lane) ?? null;
  }

  get dataChannels(): Readonly<Partial<Record<SessionChannel, BrowserDataChannel>>> {
    return Object.fromEntries(this.channels);
  }

  get channelMaterials(): BrowserSessionChannelMaterials {
    if (!this.materials) throw new Error('PAIRING_KEYS_UNAVAILABLE');
    return this.materials;
  }

  get handshakeMaterial(): BrowserSessionHandshakeMaterial {
    if (!this.handshakeMaterialValue) throw new Error('PAIRING_KEYS_UNAVAILABLE');
    return this.handshakeMaterialValue;
  }

  async connect(): Promise<void> {
    if (this.state !== 'DISCONNECTED') throw new Error('WEBRTC_SESSION_ALREADY_STARTED');
    this.setState('SIGNALING');
    try {
      await this.signaling.connect();
      this.setState('WAITING_PHONE');
    } catch (error) {
      this.fail(error);
      throw error;
    }
  }

  async waitForDataChannel(
    lane: SessionChannel = 'control',
    timeoutMs = 30_000,
  ): Promise<BrowserDataChannel> {
    const existing = this.channels.get(lane);
    if (existing) return existing;
    return new Promise<BrowserDataChannel>((resolve, reject) => {
      const timer = globalThis.setTimeout(() => {
        cleanup();
        reject(new Error('DATA_CHANNEL_TIMEOUT'));
      }, timeoutMs);
      const onDataChannel = (channel: BrowserDataChannel) => {
        cleanup();
        resolve(channel);
      };
      const cleanup = () => {
        globalThis.clearTimeout(timer);
        this.channelWaiters.get(lane)?.delete(onDataChannel);
      };
      const waiters = this.channelWaiters.get(lane) ?? new Set();
      waiters.add(onDataChannel);
      this.channelWaiters.set(lane, waiters);
    });
  }

  close(): void {
    this.peer?.close();
    this.peer = null;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
    this.channels.forEach((channel) => channel.close());
    this.channels.clear();
    this.channelWaiters.clear();
    this.signaling.close();
    this.setState('DISCONNECTED');
  }

  private enqueuePacket(packet: RelaySignalPacket): void {
    this.packetChain = this.packetChain
      .then(() => this.processPacket(packet))
      .catch((error) => this.fail(error));
  }

  private async processPacket(packet: RelaySignalPacket): Promise<void> {
    try {
      if (packet.kind === 'ready') return;
      if (packet.kind === 'phone-key-response') return await this.handleKeyResponse(packet.payload);
      if (packet.kind === 'pairing-approved') return await this.handleApproval(packet.payload);
      if (packet.kind === 'sdp-answer') return await this.handleSdpAnswer(packet.payload);
      if (packet.kind === 'ice-candidate') return await this.handleIceCandidate(packet.payload);
      throw new Error('INVALID_SIGNAL_PACKET');
    } catch (error) {
      this.fail(error);
    }
  }

  private async handleKeyResponse(payload: string): Promise<void> {
    const response = decodeProtoPayload(payload, PhoneKeyResponseV1Schema);
    const offerHash = await sha256(toBinary(PairingOfferV1Schema, this.pairing.offer));
    if (
      !equalBytes(response.sessionId, this.pairing.offer.sessionId) ||
      !equalBytes(response.offerHash, offerHash) ||
      response.phonePublicKey.byteLength !== 65 ||
      response.phonePublicKey[0] !== 4 ||
      response.expiresAtMs !== this.pairing.offer.expiresAtMs ||
      Date.now() >= Number(response.expiresAtMs)
    )
      throw new Error('INVALID_PHONE_KEY_RESPONSE');
    const sharedSecret = await deriveBrowserSharedSecret(
      this.pairing.privateKey,
      response.phonePublicKey,
    );
    const browserPublicKeyFingerprint = await sha256(this.pairing.offer.browserPublicKey);
    const phonePublicKeyFingerprint = await sha256(response.phonePublicKey);
    const browserToPhone = await deriveMaterials(
      sharedSecret,
      offerHash,
      response.phonePublicKey,
      'browser-to-phone',
    );
    const phoneToBrowser = await deriveMaterials(
      sharedSecret,
      offerHash,
      response.phonePublicKey,
      'phone-to-browser',
    );
    this.keyResponse = response;
    this.handshakeMaterialValue = {
      browserPublicKeyFingerprint,
      phonePublicKeyFingerprint,
      offerHash,
    };
    this.materials = { browserToPhone, phoneToBrowser };
  }

  private async handleApproval(payload: string): Promise<void> {
    const approval = decodeProtoPayload(payload, PairingApprovedV1Schema);
    if (!this.keyResponse || !equalBytes(approval.sessionId, this.pairing.offer.sessionId))
      throw new Error('INVALID_PAIRING_APPROVAL');
    const phoneFingerprint = await sha256(this.keyResponse.phonePublicKey);
    if (!equalBytes(approval.phonePublicKeyFingerprint, phoneFingerprint))
      throw new Error('PHONE_KEY_FINGERPRINT_MISMATCH');
    if (this.peer) return;
    this.approval = approval;
    this.setState('APPROVAL_RECEIVED');
    await this.openPeer();
  }

  private async openPeer(): Promise<void> {
    this.setState('NEGOTIATING');
    const peer = this.peerFactory(this.iceServers, this.iceTransportPolicy);
    this.peer = peer;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
    peer.onicecandidate = ({ candidate }) => {
      if (!candidate || candidate.sdpMLineIndex === null || candidate.candidate.length === 0)
        return;
      this.signaling.send({
        kind: 'ice-candidate',
        payload: JSON.stringify(candidate),
      });
    };
    peer.ondatachannel = ({ channel }) => this.installChannel(channel);
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === 'connected') this.setState('CONNECTED');
      if (peer.connectionState === 'failed' || peer.connectionState === 'closed')
        this.fail(new Error('WEBRTC_CONNECTION_FAILED'));
    };
    this.installChannel(
      peer.createDataChannel('control-v1', { ordered: true, negotiated: true, id: 1 }),
    );
    this.installChannel(
      peer.createDataChannel('file-v1', { ordered: true, negotiated: true, id: 3 }),
    );
    this.installChannel(
      peer.createDataChannel('presence-v1', {
        ordered: false,
        maxPacketLifeTime: 1000,
        negotiated: true,
        id: 5,
      }),
    );
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    const local = peer.localDescription ?? offer;
    if (!local.sdp) throw new Error('LOCAL_SDP_MISSING');
    this.signaling.send({
      kind: 'sdp-offer',
      payload: JSON.stringify({ type: local.type, sdp: local.sdp }),
    });
  }

  private installChannel(channel: BrowserDataChannel): void {
    const lane = laneForLabel(channel.label);
    if (!lane || this.channels.has(lane)) return;
    channel.binaryType = 'arraybuffer';
    this.channels.set(lane, channel);
    this.options.onDataChannel?.(channel, lane);
    this.channelWaiters.get(lane)?.forEach((listener) => listener(channel));
  }

  private async handleSdpAnswer(payload: string): Promise<void> {
    if (!this.peer) throw new Error('PEER_NOT_CREATED');
    const description = parseDescription(payload);
    if (description.type !== 'answer') throw new Error('INVALID_SDP_ANSWER');
    await this.peer.setRemoteDescription(description);
    this.remoteDescriptionSet = true;
    const pending = this.pendingRemoteIceCandidates;
    this.pendingRemoteIceCandidates = [];
    for (const candidate of pending) await this.peer.addIceCandidate(candidate);
  }

  private async handleIceCandidate(payload: string): Promise<void> {
    if (!this.peer) throw new Error('PEER_NOT_CREATED');
    const candidate = parseCandidate(payload);
    if (!this.remoteDescriptionSet) {
      this.pendingRemoteIceCandidates.push(candidate);
      return;
    }
    await this.peer.addIceCandidate(candidate);
  }

  private setState(state: BrowserWebRtcSessionState): void {
    this.state = state;
    this.options.onState?.(state);
  }

  private fail(error: unknown): void {
    const normalized = error instanceof Error ? error : new Error('WEBRTC_SESSION_FAILED');
    this.setState('FAILED');
    this.options.onError?.(normalized);
    this.peer?.close();
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
    this.signaling.close();
    this.channels.forEach((channel) => channel.close());
    this.channels.clear();
    this.channelWaiters.clear();
  }
}

function laneForLabel(label: string | undefined): SessionChannel | undefined {
  if (label === 'control-v1' || label === 'mlo-control') return 'control';
  if (label === 'file-v1') return 'file';
  if (label === 'presence-v1') return 'presence';
  return undefined;
}

function decodeProtoPayload(
  payload: string,
  schema: typeof PhoneKeyResponseV1Schema,
): PhoneKeyResponseV1;
function decodeProtoPayload(
  payload: string,
  schema: typeof PairingApprovedV1Schema,
): PairingApprovedV1;
function decodeProtoPayload(
  payload: string,
  schema: typeof PhoneKeyResponseV1Schema | typeof PairingApprovedV1Schema,
): PhoneKeyResponseV1 | PairingApprovedV1 {
  const bytes = decodeBase64Url(payload);
  return fromBinary(schema, bytes);
}

async function deriveMaterials(
  sharedSecret: Uint8Array,
  offerHash: Uint8Array,
  phonePublicKey: Uint8Array,
  direction: 'browser-to-phone' | 'phone-to-browser',
): Promise<Record<SessionChannel, SessionChannelMaterial>> {
  const channels: SessionChannel[] = ['control', 'file', 'presence'];
  const values = await Promise.all(
    channels.map(
      async (channel) =>
        [
          channel,
          await deriveSessionChannelMaterial(
            sharedSecret,
            offerHash,
            phonePublicKey,
            direction,
            channel,
          ),
        ] as const,
    ),
  );
  return Object.fromEntries(values) as Record<SessionChannel, SessionChannelMaterial>;
}

function parseDescription(payload: string): BrowserSessionDescription {
  const value = parseJsonRecord(payload);
  if (typeof value.type !== 'string' || typeof value.sdp !== 'string')
    throw new Error('INVALID_SDP_PAYLOAD');
  if (!['offer', 'answer', 'pranswer', 'rollback'].includes(value.type))
    throw new Error('INVALID_SDP_PAYLOAD');
  return { type: value.type as BrowserSessionDescription['type'], sdp: value.sdp };
}

function parseCandidate(payload: string): BrowserIceCandidate {
  const value = parseJsonRecord(payload);
  if (
    typeof value.candidate !== 'string' ||
    (value.sdpMid !== null && typeof value.sdpMid !== 'string') ||
    typeof value.sdpMLineIndex !== 'number'
  )
    throw new Error('INVALID_ICE_PAYLOAD');
  return {
    candidate: value.candidate,
    sdpMid: value.sdpMid,
    sdpMLineIndex: value.sdpMLineIndex,
  };
}

function parseJsonRecord(payload: string): Record<string, unknown> {
  if (payload.length > 32 * 1024) throw new Error('SIGNAL_PAYLOAD_TOO_LARGE');
  let value: unknown;
  try {
    value = JSON.parse(payload);
  } catch {
    throw new Error('INVALID_SIGNAL_PAYLOAD');
  }
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('INVALID_SIGNAL_PAYLOAD');
  return value as Record<string, unknown>;
}

function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value) || value.length % 4 === 1)
    throw new Error('INVALID_SIGNAL_PAYLOAD');
  const binary = atob(
    value
      .replaceAll('-', '+')
      .replaceAll('_', '/')
      .padEnd(Math.ceil(value.length / 4) * 4, '='),
  );
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function validateIceServers(value: readonly RTCIceServer[]): readonly RTCIceServer[] {
  if (value.length > 8) throw new Error('TOO_MANY_ICE_SERVERS');
  value.forEach((server) => {
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    if (
      urls.length === 0 ||
      urls.length > 8 ||
      urls.some((url) => typeof url !== 'string' || !/^(stun|stuns|turn|turns):/u.test(url))
    ) {
      throw new Error('INVALID_ICE_SERVER');
    }
    if ((server.username === undefined) !== (server.credential === undefined)) {
      throw new Error('ICE_CREDENTIAL_PAIR_REQUIRED');
    }
    if (
      urls.some((url) => /^(turn|turns):/u.test(url)) &&
      (typeof server.username !== 'string' || typeof server.credential !== 'string')
    ) {
      throw new Error('TURN_CREDENTIALS_REQUIRED');
    }
    if (typeof server.username === 'string' && server.username.length > 200)
      throw new Error('INVALID_ICE_USERNAME');
    if (typeof server.credential === 'string' && server.credential.length > 200)
      throw new Error('INVALID_ICE_CREDENTIAL');
  });
  return value;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.byteLength === right.byteLength && left.every((value, index) => value === right[index])
  );
}
